import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Underline } from '@tiptap/extension-underline';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { FontFamily } from '@tiptap/extension-font-family';
import {
  Bold as IcBold, Italic as IcItalic, Underline as IcUnderline, Strikethrough as IcStrike,
  AlignLeft as IcAlignL, AlignCenter as IcAlignC, AlignRight as IcAlignR,
  List as IcList, ListOrdered as IcListOrdered,
  Link as IcLink, Image as IcImage, Table as IcTable, Paperclip as IcAttach,
  Quote as IcQuote, Code as IcCode, Minus as IcDivider,
  Library as IcSnippets, Sparkles as IcAI,
  Undo as IcUndo, Redo as IcRedo, Eraser as IcEraser, Maximize2 as IcMax, Minimize2 as IcMin,
  Highlighter as IcHighlight, ChevronDown as IcChevron,
} from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const EDITOR_STYLES = `
.ce-editor-wrap { border: 1px solid #d4d4d4; border-radius: 6px; background: #fff; overflow: hidden; transition: border-color 0.15s; }
.ce-editor-wrap:focus-within { border-color: #8B1A1A; box-shadow: 0 0 0 3px rgba(139, 26, 26, 0.08); }
.ce-editor-wrap.ce-fullscreen { position: fixed; top: 20px; left: 20px; right: 20px; bottom: 20px; z-index: 9999; box-shadow: 0 20px 60px rgba(0,0,0,0.3); display: flex; flex-direction: column; }
.ce-editor-wrap.ce-fullscreen .ce-editor-body { flex: 1; overflow-y: auto; }
.ce-toolbar-row { display: flex; align-items: center; gap: 2px; flex-wrap: wrap; padding: 5px 8px; border-bottom: 1px solid #ececec; background: #fafafa; }
.ce-toolbar-row:last-of-type { border-bottom: 1px solid #e5e5e5; }
.ce-tb-btn { width: 28px; height: 28px; border: none; background: transparent; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #555; font-size: 13px; padding: 0; transition: background 0.1s; }
.ce-tb-btn:hover { background: #ececec; color: #111; }
.ce-tb-btn.active { background: #111; color: #fff; }
.ce-tb-btn.active:hover { background: #333; }
.ce-tb-btn-wide { width: auto; padding: 0 10px; gap: 6px; font-size: 12.5px; }
.ce-tb-btn[disabled] { opacity: 0.35; cursor: not-allowed; }
.ce-tb-btn[disabled]:hover { background: transparent; }
.ce-tb-sep { width: 1px; height: 18px; background: #d4d4d4; margin: 0 4px; }
.ce-tb-select { height: 28px; font-size: 12.5px; padding: 0 6px; border: 1px solid #d4d4d4; border-radius: 4px; background: #fff; cursor: pointer; color: #333; }
.ce-tb-color-swatch { width: 14px; height: 14px; border-radius: 3px; border: 1px solid #d4d4d4; }
.ce-tb-spacer { flex: 1; }
.ce-editor-body { padding: 14px 16px; min-height: 160px; font-size: 14px; line-height: 1.6; color: #222; }
.ce-editor-body .ProseMirror { outline: none; min-height: 130px; }
.ce-editor-body .ProseMirror p { margin: 0 0 8px 0; }
.ce-editor-body .ProseMirror p:last-child { margin-bottom: 0; }
.ce-editor-body .ProseMirror h1 { font-size: 22px; font-weight: 600; margin: 12px 0 8px; }
.ce-editor-body .ProseMirror h2 { font-size: 18px; font-weight: 600; margin: 12px 0 8px; }
.ce-editor-body .ProseMirror h3 { font-size: 15px; font-weight: 600; margin: 10px 0 6px; }
.ce-editor-body .ProseMirror ul, .ce-editor-body .ProseMirror ol { padding-left: 22px; margin: 0 0 8px 0; }
.ce-editor-body .ProseMirror li { margin: 2px 0; }
.ce-editor-body .ProseMirror blockquote { border-left: 3px solid #d4d4d4; padding-left: 12px; margin: 8px 0; color: #555; font-style: italic; }
.ce-editor-body .ProseMirror code { background: #f4f4f4; padding: 1px 5px; border-radius: 3px; font-size: 0.9em; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.ce-editor-body .ProseMirror pre { background: #f4f4f4; padding: 10px 12px; border-radius: 4px; margin: 8px 0; overflow-x: auto; }
.ce-editor-body .ProseMirror pre code { background: transparent; padding: 0; }
.ce-editor-body .ProseMirror hr { border: none; border-top: 1px solid #d4d4d4; margin: 14px 0; }
.ce-editor-body .ProseMirror a { color: #8B1A1A; text-decoration: underline; }
.ce-editor-body .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #aaa; float: left; height: 0; pointer-events: none; }
.ce-editor-body .ProseMirror mark { background: #fff3a3; padding: 0 2px; border-radius: 2px; }
.ce-signature-preview { border-top: 1px dashed #d4d4d4; margin-top: 14px; padding-top: 10px; font-size: 12.5px; color: #777; white-space: pre-wrap; }
`;

function TbBtn({ active, disabled, onClick, title, children, wide }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`ce-tb-btn ${wide ? 'ce-tb-btn-wide' : ''} ${active ? 'active' : ''}`}
    >{children}</button>
  );
}

export default function ReplyEditor({ value, onChange, placeholder, signatureHtml, clientId }) {
  const { getToken } = useAuth();
  const [fullscreen, setFullscreen] = useState(false);
  const [showColorMenu, setShowColorMenu] = useState(false);
  const colorMenuRef = useRef(null);
  const [snippets, setSnippets] = useState([]);
  const [showSnippets, setShowSnippets] = useState(false);
  const [snippetsLoading, setSnippetsLoading] = useState(false);
  const snippetRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
      Placeholder.configure({ placeholder: placeholder || 'Type your reply…' }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      FontFamily,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  // Sync external value -> editor without re-emitting onChange (avoids clobber loop).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming !== current) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (colorMenuRef.current && !colorMenuRef.current.contains(e.target)) setShowColorMenu(false);
      if (snippetRef.current && !snippetRef.current.contains(e.target)) setShowSnippets(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!editor) return <div style={{ padding: 12, color: '#999', fontSize: 13 }}>Loading editor…</div>;

  const setBlock = (val) => {
    if (val === 'p') editor.chain().focus().setParagraph().run();
    else if (val === 'h1') editor.chain().focus().toggleHeading({ level: 1 }).run();
    else if (val === 'h2') editor.chain().focus().toggleHeading({ level: 2 }).run();
    else if (val === 'h3') editor.chain().focus().toggleHeading({ level: 3 }).run();
  };

  const currentBlock = editor.isActive('heading', { level: 1 }) ? 'h1'
    : editor.isActive('heading', { level: 2 }) ? 'h2'
    : editor.isActive('heading', { level: 3 }) ? 'h3'
    : 'p';

  const setFont = (font) => {
    if (font === 'default') editor.chain().focus().unsetFontFamily().run();
    else editor.chain().focus().setFontFamily(font).run();
  };

  const handleLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL', prev || 'https://');
    if (url === null) return;
    if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const showStub = (label) => alert(`${label} — coming soon`);

  const openSnippets = async () => {
    const next = !showSnippets;
    setShowSnippets(next);
    if (next && clientId) {
      setSnippetsLoading(true);
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/clients/${clientId}/snippets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setSnippets(await r.json());
      } catch (_) { /* leave list as-is */ }
      finally { setSnippetsLoading(false); }
    }
  };

  const insertSnippet = async (snip) => {
    const htmlToInsert = snip.body_html || (snip.body_text ? `<p>${snip.body_text}</p>` : '');
    editor.chain().focus().insertContent(htmlToInsert).run();
    setShowSnippets(false);
    try {
      const token = await getToken();
      fetch(`${API_BASE}/snippets/${snip.id}/use`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    } catch (_) {}
  };

  const colors = ['#222', '#8B1A1A', '#0e7490', '#1a7f3c', '#a16207', '#6d28d9', '#b91c1c'];

  return (
    <>
      <style>{EDITOR_STYLES}</style>
      <div className={`ce-editor-wrap ${fullscreen ? 'ce-fullscreen' : ''}`}>

        <div className="ce-toolbar-row">
          <select className="ce-tb-select" value={currentBlock} onChange={(e) => setBlock(e.target.value)} title="Text style">
            <option value="p">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>
          <select className="ce-tb-select" defaultValue="default" onChange={(e) => setFont(e.target.value)} title="Font" style={{ marginLeft: 4 }}>
            <option value="default">Default</option>
            <option value="Inter, sans-serif">Inter</option>
            <option value="Arial, sans-serif">Arial</option>
            <option value="Georgia, serif">Georgia</option>
            <option value="ui-monospace, monospace">Monospace</option>
          </select>

          <div className="ce-tb-sep" />

          <TbBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (⌘B)"><IcBold size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (⌘I)"><IcItalic size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline (⌘U)"><IcUnderline size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough"><IcStrike size={15} strokeWidth={2.2} /></TbBtn>

          <div ref={colorMenuRef} style={{ position: 'relative' }}>
            <TbBtn onClick={() => setShowColorMenu(s => !s)} title="Text colour" wide>
              <span className="ce-tb-color-swatch" style={{ background: editor.getAttributes('textStyle').color || '#222' }} />
              <IcChevron size={12} />
            </TbBtn>
            {showColorMenu && (
              <div style={{ position: 'absolute', top: 32, left: 0, background: '#fff', border: '1px solid #d4d4d4', borderRadius: 4, padding: 6, display: 'flex', gap: 4, zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                {colors.map(c => (
                  <button key={c} type="button" onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { editor.chain().focus().setColor(c).run(); setShowColorMenu(false); }}
                    style={{ width: 20, height: 20, background: c, border: '1px solid #d4d4d4', borderRadius: 3, cursor: 'pointer' }} />
                ))}
                <button type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { editor.chain().focus().unsetColor().run(); setShowColorMenu(false); }}
                  title="Clear colour"
                  style={{ width: 20, height: 20, background: '#fff', border: '1px solid #d4d4d4', borderRadius: 3, cursor: 'pointer', fontSize: 12 }}>×</button>
              </div>
            )}
          </div>
          <TbBtn active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Highlight"><IcHighlight size={15} strokeWidth={2.2} /></TbBtn>

          <div className="ce-tb-sep" />

          <TbBtn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align left"><IcAlignL size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align centre"><IcAlignC size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align right"><IcAlignR size={15} strokeWidth={2.2} /></TbBtn>

          <div className="ce-tb-sep" />

          <TbBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list"><IcList size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><IcListOrdered size={15} strokeWidth={2.2} /></TbBtn>
        </div>

        <div className="ce-toolbar-row">
          <TbBtn wide onClick={handleLink} active={editor.isActive('link')} title="Insert link (⌘K)"><IcLink size={14} strokeWidth={2.2} />Link</TbBtn>
          <TbBtn wide onClick={() => showStub('Image upload')} title="Insert image"><IcImage size={14} strokeWidth={2.2} />Image</TbBtn>
          <TbBtn wide onClick={() => showStub('Table insert')} title="Insert table"><IcTable size={14} strokeWidth={2.2} />Table</TbBtn>
          <TbBtn wide onClick={() => showStub('Attachment')} title="Attach file"><IcAttach size={14} strokeWidth={2.2} />Attach</TbBtn>

          <div className="ce-tb-sep" />

          <TbBtn wide active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote"><IcQuote size={14} strokeWidth={2.2} />Quote</TbBtn>
          <TbBtn wide active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code block"><IcCode size={14} strokeWidth={2.2} />Code</TbBtn>
          <TbBtn wide onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule"><IcDivider size={14} strokeWidth={2.2} />Divider</TbBtn>

          <div className="ce-tb-sep" />

          <div ref={snippetRef} style={{ position: 'relative' }}>
            <TbBtn wide onClick={clientId ? openSnippets : () => showStub('Snippets')} active={showSnippets} title="Insert snippet"><IcSnippets size={14} strokeWidth={2.2} />Snippets</TbBtn>
            {showSnippets && clientId && (
              <div style={{ position: 'absolute', top: 34, left: 0, width: 320, maxHeight: 300, overflowY: 'auto', background: '#fff', border: '1px solid #d4d4d4', borderRadius: 8, zIndex: 20, boxShadow: '0 6px 20px rgba(0,0,0,0.12)' }}>
                {snippetsLoading ? (
                  <div style={{ padding: 14, fontSize: 13, color: '#999' }}>Loading…</div>
                ) : snippets.length === 0 ? (
                  <div style={{ padding: 14, fontSize: 13, color: '#999' }}>No snippets yet.</div>
                ) : snippets.map((sn) => (
                  <button key={sn.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertSnippet(sn)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: '1px solid #F2F2F2', background: '#fff', cursor: 'pointer' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>{sn.title}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sn.body_text || ''}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <TbBtn wide onClick={() => showStub('AI rewrite')} title="AI rewrite"><IcAI size={14} strokeWidth={2.2} />AI rewrite</TbBtn>

          <div className="ce-tb-spacer" />

          <TbBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (⌘Z)"><IcUndo size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (⌘⇧Z)"><IcRedo size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting"><IcEraser size={15} strokeWidth={2.2} /></TbBtn>
          <TbBtn onClick={() => setFullscreen(f => !f)} title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>{fullscreen ? <IcMin size={15} strokeWidth={2.2} /> : <IcMax size={15} strokeWidth={2.2} />}</TbBtn>
        </div>

        <div className="ce-editor-body">
          <EditorContent editor={editor} />
          {signatureHtml && (
            <div className="ce-signature-preview" dangerouslySetInnerHTML={{ __html: signatureHtml }} />
          )}
        </div>

      </div>
    </>
  );
}
